/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains a hook that proxies a hook from 
 * online-patient-management-reducers making less types required to use the hook.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import { faPaintRoller } from '@fortawesome/pro-duotone-svg-icons';
import { faDownload } from '@fortawesome/pro-duotone-svg-icons/faDownload';
import { faEye } from '@fortawesome/pro-duotone-svg-icons/faEye';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormHelperText, LinearProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import { Theme, darken, makeStyles } from '@material-ui/core/styles';
import { onlinePatientManagement } from '@ngt/online-patient-management-ui';
import { WindowManager, WindowReceivedEvent } from '@ngt/redact';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { FieldRenderProps, useField } from 'react-final-form';
import { v4 as uuidv4 } from 'uuid';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../../api/Neon.Dtos';
import { pascalToCamelCase } from '../OpmsInput';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

export const TYPE_DEFAULT = 'DEFAULT';

type FileUploadType = typeof TYPE_DEFAULT;

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

type FileUploadsProps = FieldRenderProps<any, any>

interface IFileUploadProps extends FileUploadsProps {
    display?: FileUploadType;
    fileIdFieldName: string;
    imageFieldName: string;
    existsFieldName: string;
    onChange?: (value?: string) => void;
    disabled?: boolean;
    downloadLink?: string;
    viewLink?: string;
    allowRedactOnFormDisable?: boolean;
    onRedactFinish?: () => void;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    input: {
        display: 'none'
    },
    button: {
        padding: theme.spacing(0),
        cursor: 'pointer'
    },
    label: {
        width: '100%',
        padding: theme.spacing(0.75, 2)
    },
    buttonFailed: {
        background: theme.palette.error.main,
        color: theme.palette.common.white,

        '&:hover': {
            background: darken(theme.palette.error.main, 0.20)
        }
    },
    uploadingButton: {
        cursor: 'pointer',
        padding: theme.spacing(0, 2)
    },
    progress: {
        height: theme.spacing(1.5)
    },
    icon: {
        minWidth: 0,
        padding: theme.spacing(0, 1.25),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        marginLeft: theme.spacing(2),

        '& > *': {
            opacity: 0.9
        }
    },
    container: {
        display: 'flex'
    },
    uploadContainer: {
        flex: '1 1 auto'
    },
    error: {
        color: theme.palette.error.main
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const FileUpload: React.FunctionComponent<IFileUploadProps> = ({
    display,
    input: { name, value, onChange: onInputChange, onFocus, onBlur },
    meta,
    onChange,
    fileIdFieldName,
    existsFieldName,
    imageFieldName,
    render,
    disabled,
    downloadLink,
    viewLink,
    allowRedactOnFormDisable,
    onRedactFinish,
    ...rest
}) => {
    const classes = useStyles();

    const { enqueueSnackbar } = useSnackbar();

    const parentName = React.useMemo(() => {
        return name.split('.').filter((namePart, index, nameParts) => index !== nameParts.length - 1).join('.');
    }, [name]);

    const completeExistsFieldName = React.useMemo(() => {
        return `${parentName}.${pascalToCamelCase(existsFieldName)}`;
    }, [existsFieldName, parentName]);

    const completeImageFieldName = React.useMemo(() => {
        return `${parentName}.${pascalToCamelCase(imageFieldName)}`;
    }, [imageFieldName, parentName]);

    const completeFileIdFieldName = React.useMemo(() => {
        return `${parentName}.${pascalToCamelCase(fileIdFieldName)}`;
    }, [fileIdFieldName, parentName]);

    const completeExistsName = React.useMemo(() => {
        return `${parentName}.name`;
    }, [parentName]);

    const completeExistsExt = React.useMemo(() => {
        return `${parentName}.extension`;
    }, [parentName]);

    const { input: { value: existsName } } = useField<string | null | undefined>(completeExistsName, { subscription: { value: true } });
    const { input: { value: existsExt } } = useField<string | null | undefined>(completeExistsExt, { subscription: { value: true } });
    const { input: { value: existsValue } } = useField<number | null | undefined>(completeExistsFieldName, { subscription: { value: true } });
    const { input: { value: fileIdValue } } = useField<number | null | undefined>(completeFileIdFieldName, { subscription: { value: true } });
    const { input: { value: imageValue } } = useField<number | null | undefined>(completeImageFieldName, { subscription: { value: true } });

    const [failed, setFailed] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [request, setRequest] = React.useState<XMLHttpRequest | null>(null);
    const [temporaryFile, setTemporaryFile] = React.useState<Dtos.TemporaryFile | null>(null);

    const inputRef = React.useRef<HTMLInputElement>(null);

    const windowManagerRef = React.useRef<typeof WindowManager | null>(null);

    const onUploadKeyPress = React.useCallback((event?: React.KeyboardEvent<HTMLButtonElement>) => {
        event?.preventDefault();

        if (event?.charCode === 32) {
            inputRef.current?.click();
        }
    }, []);

    const onUploadClick = React.useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.preventDefault();

        inputRef.current?.click();
    }, []);

    const onChangeCombined = React.useCallback((guid?: string) => {
        if (onChange) {
            onChange(guid);
        }

        onInputChange(guid);
    }, [onChange, onInputChange]);

    const cancelUpload = React.useCallback(() => {
        request?.abort()
        setRequest(null);

        setTemporaryFile(null);
        setUploading(false);
        setProgress(0);
        setFailed(false);
    }, [request, setRequest, setProgress, setUploading, setTemporaryFile])

    const processUploadResponse = React.useCallback((responseStr: string) => {
        try {
            const result: Dtos.UploadTempFileResponse = JSON.parse(responseStr);

            if (!result.responseStatus) {
                const guid = result.guid;

                onlinePatientManagement.client
                    .get(new Dtos.GetTempFileInfo({ guid }))
                    .then(response => {
                        onChangeCombined(guid);
                        setTemporaryFile(response.temporaryFile);
                        setUploading(false);
                        setProgress(0);
                        setRequest(null);
                        setFailed(false);
                    })
                    .catch(error => {
                        setUploading(false);
                        setProgress(0);
                        setRequest(null);
                        setFailed(true);
                    });
            }
            else {
                setUploading(false);
                setProgress(0);
                setRequest(null);
                setFailed(true);
            }
        }
        catch {
            setUploading(false);
            setProgress(0);
            setRequest(null);
            setFailed(true);
        }
    }, [setUploading, setFailed, setProgress, setRequest, setTemporaryFile, onChangeCombined])

    const uploadFile = React.useCallback((file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('json', 'true');

        const xhr = new XMLHttpRequest();

        if (xhr.upload) {
            xhr.upload.onprogress = (event) => {
                const done = (event as any).position || event.loaded;
                const total = (event as any).totalSize || event.total;

                const percentage = (Math.floor(done / total * 1000) / 10);

                setProgress(percentage);

                console.log('xhr.upload progress: ' + done + ' / ' + total + ' = ' + (Math.floor(done / total * 1000) / 10) + '%');
            };
        }

        xhr.onreadystatechange = (event) => {
            if (4 === xhr.readyState) {
                if (xhr.status === 200) {
                    console.log(['xhr upload complete', event]);
                    processUploadResponse(xhr.responseText);
                }
                else if (xhr.status !== 0) {
                    setUploading(false);
                    setFailed(true);
                    setProgress(0);
                }
            }
        };

        xhr.open('post', '/api/file/temp/upload?format=json', true);

        xhr.send(formData);

        setRequest(xhr);
    }, [setProgress, setFailed, setRequest, setUploading, processUploadResponse, disabled, onRedactFinish]);

    const onFileChange = React.useCallback((event?: React.ChangeEvent<HTMLInputElement>) => {
        cancelUpload();

        if (event?.target.value && event?.target.value !== '') {
            const file = event.target.files?.item(0);

            if (file) {
                setUploading(true);
                setProgress(0);
                onChangeCombined(undefined);
                setFailed(false);
                setTemporaryFile(null);

                uploadFile(file)
                return;
            }
        }

        setUploading(false);
        setProgress(0);
        onChangeCombined(undefined);
        setFailed(false);
        setTemporaryFile(null);
    }, [setUploading, onChangeCombined, setProgress, cancelUpload, setFailed, uploadFile, setTemporaryFile])

    const onFailedClick = React.useCallback(() => {
        setFailed(false);
    }, [setFailed]);

    const handleRedactClick = React.useCallback((sourceUrl: string, fileName: string) => {
        // BCTNEON-432. If form has been disabled, prevent redaction from occurring
        if (disabled && allowRedactOnFormDisable !== true) {
            enqueueSnackbar("Please contact the trial team for further action on redacting this document.", { variant: "warning", key: 'redact-denied' });
            return;
        }

        if (windowManagerRef.current) {
            windowManagerRef.current.dispose();
        }

        const percentageWidth = 0.5;
        const percentageHeight = 0.9;

        // Fixes dual-screen position                             Most browsers      Firefox
        const dualScreenLeft = window.screenLeft !==  undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop !==  undefined   ? window.screenTop  : window.screenY;

        const screenWidth = window.innerWidth ? window.innerWidth : window.screen.width;
        const screenHeight = window.innerHeight ? window.innerHeight : window.screen.height;

        const width = screenWidth * percentageWidth;
        const height = screenHeight * percentageHeight;

        const systemZoom = screenWidth / window.screen.availWidth;
        const left = (screenWidth - width) / 2 / systemZoom + dualScreenLeft
        const top = (screenHeight - height) / 2 / systemZoom + dualScreenTop

        var manager = WindowManager;

        manager.init({
            isOC: true,
            targetUrl: (window as any).REDACT_URL,
            hostOrigin: window.location.origin,
            instanceId: uuidv4(),
            windowName: 'redaction_window',
            windowOptions: {
                width: (width / systemZoom).toString(),
                height: (height / systemZoom).toString(),
                top: top.toString(),
                left: left.toString()
            },
            onInit: async () => {
                try {
                    const blob = await fetch(`${window.location.origin}${sourceUrl}`).then(res => res.blob());

                    var fileObj = new File([blob], fileName);

                    if (!fileObj || !(fileObj instanceof File)) {
                        throw new Error('Failed to retrieve file');
                    }

                    manager.postMessage<File>(fileObj, WindowReceivedEvent.Update);
                } 
                catch(error: any) {
                    manager.dispose();
                }
            },
            onUpdate: async (data) => {
                try {
                    if (!!!data) {
                        throw new Error('File failed to redact');
                    }

                    uploadFile(data.payload);

                    if (disabled && allowRedactOnFormDisable) {
                        onRedactFinish?.();
                    }

                    manager.dispose();
                    
                } catch (error: any) {
                    manager.dispose();
                }
            },
            onFinish: () => {
                manager.dispose();
            },
            onError: (message) => {
                manager.dispose()
            }
        });
        
        manager.open();

        windowManagerRef.current = manager;
        
    }, [uploadFile, allowRedactOnFormDisable, onRedactFinish]);

    const showError = meta.touched && (meta.error || (meta.submitError && !meta.dirtySinceLastSubmit)) && !meta.submitting;

    if (uploading) {
        return (
            <>
                <Button
                    variant="contained"
                    component="button"
                    className={classes.uploadingButton}
                    fullWidth
                    color="primary"
                    onClick={cancelUpload}
                >
                    Cancel
                </Button>
                <LinearProgress
                    className={classes.progress}
                    value={progress}
                    color="secondary"
                    variant="determinate"
                />
            </>
        );
    }

    if (failed) {
        return (
            <Button
                variant="contained"
                component="button"
                fullWidth
                className={classes.buttonFailed}
                color="inherit"
                onClick={onFailedClick}
            >
                Upload Failed
            </Button>
        );
    }

    if (temporaryFile && value) {
        return (
            <div
                className={classes.container}
            >
                <div
                    className={classes.uploadContainer}
                >
                    <Button
                        variant="contained"
                        component="button"
                        fullWidth
                        color="primary"
                        onClick={cancelUpload}
                        disabled={disabled}
                    >
                        Reset Upload
                    </Button>
                </div>
                {
                    temporaryFile.image && (
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.icon}
                            component="a"
                            href={`/api/file/temp/get/${value}`}
                            target="_blank"
                        >
                            <FontAwesomeIcon icon={faEye} fixedWidth />
                        </Button>
                    )
                }
                <Button
                    variant="contained"
                    color="secondary"
                    className={classes.icon}
                    component="a"
                    href={`/api/file/temp/download/${value}`}
                >
                    <FontAwesomeIcon icon={faDownload} fixedWidth />
                </Button>
                {
                    temporaryFile.extension === ".pdf" && 
                    <Button
                        variant="contained"
                        color="secondary"
                        className={classes.icon}
                        onClick={() => handleRedactClick(`/api/file/temp/download/${value}`, temporaryFile.name)}
                    >
                        <FontAwesomeIcon icon={faPaintRoller} fixedWidth />
                    </Button>
                }
            </div>
        );
    }

    return (
        <>
            <div
                className={classes.container}
            >
                <div
                    className={classes.uploadContainer}
                >
                    <input
                        ref={inputRef}
                        name={name}
                        id={name}
                        className={classes.input}
                        type="file"
                        onBlur={onBlur}
                        onFocus={onFocus}
                        onChange={onFileChange}
                        disabled={disabled}
                    />
                    <Button
                        variant="contained"
                        className={classes.button}
                        component="button"
                        fullWidth
                        color="primary"
                        disabled={disabled}
                        onKeyPress={onUploadKeyPress}
                        onClick={onUploadClick}
                    >
                        <label
                            htmlFor={name}
                            className={classes.label}
                        >
                            {
                                existsValue ?
                                    'Upload New' :
                                    'Upload'
                            }
                        </label>
                    </Button>
                </div>
                {
                    !!existsValue && imageValue && (
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.icon}
                            component="a"
                            href={!!viewLink ? `${viewLink}/${fileIdValue}` : `/source-document/${fileIdValue}`}
                            target="_blank"
                        >
                            <FontAwesomeIcon icon={faEye} fixedWidth />
                        </Button>
                    )
                }
                {
                    !!existsValue && (
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.icon}
                            component="a"
                            href={!!downloadLink ? `${downloadLink}/${fileIdValue}` : `/source-document/download/${fileIdValue}`}
                        >
                            <FontAwesomeIcon icon={faDownload} fixedWidth />
                        </Button>
                    )
                }
                {
                    existsValue && existsName && existsExt && existsExt === ".pdf" &&  
                    <Button
                        variant="contained"
                        color="secondary"
                        className={classes.icon}
                            onClick={() => handleRedactClick((!!downloadLink ? `${downloadLink}/${fileIdValue}` : `/source-document/download/${fileIdValue}`), `${existsName}${existsExt}`)}
                    >
                        <FontAwesomeIcon icon={faPaintRoller} fixedWidth />
                    </Button>
                }
            </div>
            {
                !!showError && <FormHelperText className={classes.error}>{meta.error}</FormHelperText>
            }
        </>
    );
}

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */

export default FileUpload;
